import { TextLinkSpan } from "@/components";
import { H3, H5 } from "@/components/headings";
import { BtnGroup } from "@/components/module-visual-editor/shared-components";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ColorBadge } from "@/components/ui/color-badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  OrganizationDto,
  UserWorkspaceDto,
} from "@/lib/main-rest-client/definitions";
import {
  canAdmin,
  cn,
  DEFAULT_ICON_SIZE,
  generateWorkspaceName,
  getWorkspaceUrl,
  NormIcons,
  setClipboard,
  slugify,
  toastSuccess,
} from "@/lib/utils";
import { useOrganizationRepositoryAdapters } from "@/queries/repository-adapters";
import {
  useCreateFullWorkspace,
  useDeleteWorkspace,
} from "@/queries/workspaces";
import { Link } from "@tanstack/react-router";
import { CheckCheck, X } from "lucide-react";
import { ComponentProps } from "react";
import { toast } from "sonner";

// IMPORTANT! Don't unmount this component too early
// the query to create a workspace need to dispatch three queries
// If the component is unmounted early the queries don't complete
function NoWorkspace({ className, ...rest }: ComponentProps<typeof Card>) {
  const createWorkspaceMutation = useCreateFullWorkspace();

  const handleClick = () => {
    const workspaceName = generateWorkspaceName();
    createWorkspaceMutation.mutate({
      createWorkspaceDto: {
        name: workspaceName,
        slug: slugify(workspaceName),
        access_level: "public_read",
      },
    });
  };

  return (
    <Card
      role="button"
      className={cn("hover:bg-accent", className)}
      onClick={handleClick}
      {...rest}
    >
      <CardHeader>
        <CardDescription className="text-center py-8 text-muted-foreground pb-2 flex-col flex items-center gap-2">
          Workspaces organize endpoints for your projects.
          <Button size="sm">
            <NormIcons.Add size={DEFAULT_ICON_SIZE} className="mr-2" />
            Create workspace
          </Button>
        </CardDescription>
      </CardHeader>
    </Card>
  );
}

function WorkspaceCard({
  workspace,
  organizationDto,
  isConnectedToVersionControl,
}: {
  workspace: UserWorkspaceDto;
  organizationDto: OrganizationDto;
  isConnectedToVersionControl: boolean;
}) {
  const workspaceDeleteMutation = useDeleteWorkspace();

  const handleDelete = (id: string) => {
    workspaceDeleteMutation.mutate({ workspaceId: id });
  };

  const handleCopy = async () => {
    const workspaceLink = getWorkspaceUrl({
      workspaceSlug: workspace.slug,
      organizationSlug: organizationDto.slug,
    });
    await setClipboard(workspaceLink);
    toast.success("Copied workspace url.");
  };

  return (
    <Card className="flex flex-col items-stretch overflow-hidden">
      <CardHeader className="grow">
        <div className="flex justify-between pb-4 items-start">
          <div className="pb-8">
            <H3 className="text-muted-foreground pb-2">Workspace</H3>
            <CardTitle className="break-all font-mono pb-2">
              {workspace.name}
            </CardTitle>

            {isConnectedToVersionControl ? (
              <Button variant="ghost" disabled className="gap-2 pl-0">
                <CheckCheck className="text-green-400" size="12" />
                Connected to Github
              </Button>
            ) : (
              <Button variant="ghost" disabled className="gap-2 pl-0">
                <X className="text-red-400" size="12" />
                Not connected to Github
              </Button>
            )}
          </div>
          <BtnGroup className="gap-2">
            <Link
              to="/dash/$organizationSlug/$workspaceSlug/edit"
              params={{
                workspaceSlug: workspace.slug,
                organizationSlug: organizationDto.slug,
              }}
            >
              <Button variant="ghost" size="icon-sm">
                <NormIcons.Edit size={DEFAULT_ICON_SIZE} />
              </Button>
            </Link>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button size="icon-sm" variant="ghost" onClick={handleCopy}>
                  <NormIcons.Copy size={DEFAULT_ICON_SIZE} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Copy workspace link</TooltipContent>
            </Tooltip>
            {canAdmin(workspace.role) && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="ghost" className="shrink-0" size="icon-sm">
                    <NormIcons.Delete size={DEFAULT_ICON_SIZE} />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete this workspace?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone. This will permanently delete
                      your workspace and remove your data from our servers.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => handleDelete(workspace.id)}
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </BtnGroup>
        </div>
        <div className="grid grid-cols-[max-content,1fr] gap-2">
          <H5 className="text-muted-foreground">Org slug</H5>
          <div>
            <ColorBadge color="muted">{workspace.organization_slug}</ColorBadge>
          </div>
          <H5 className="text-muted-foreground">Your role</H5>
          <div>
            {workspace.role === "reader" && (
              <ColorBadge color="muted">Reader</ColorBadge>
            )}
            {workspace.role === "editor" && (
              <ColorBadge color="muted">Editor</ColorBadge>
            )}
            {workspace.role === "admin" && (
              <ColorBadge color="muted">Admin</ColorBadge>
            )}
          </div>
          <H5 className="text-muted-foreground">Workspace privacy</H5>
          <div>
            {workspace.access_level === "public_write" && (
              <ColorBadge color="muted">Public write</ColorBadge>
            )}
            {workspace.access_level === "public_read" && (
              <ColorBadge color="muted">Public read</ColorBadge>
            )}
            {workspace.access_level === "private_read" && (
              <ColorBadge color="muted">Private read</ColorBadge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Link
          to="/editor/$organizationSlug/$workspaceSlug"
          params={{
            organizationSlug: workspace.organization_slug,
            workspaceSlug: workspace.slug,
          }}
        >
          <Button className="w-full" variant="outline" size="sm">
            Open
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
}

type WorkspaceListProps = {
  workspaceList: UserWorkspaceDto[];
  organizationDto: OrganizationDto;
};

export function WorkspaceList({
  workspaceList,
  organizationDto,
}: WorkspaceListProps) {
  const repositoryAdaptersQuery = useOrganizationRepositoryAdapters();
  const createEmptyWorkspaceMutation = useCreateFullWorkspace();

  const hasReachedLimit = workspaceList.length >= 5;

  return (
    <div>
      {/* DO NOT UNMOUNT THIS COMPONENT EARLY */}
      <NoWorkspace className={cn({ hidden: workspaceList.length > 0 })} />
      {/* END */}
      <div className={cn("pt-4", { hidden: workspaceList.length === 0 })}>
        <div className="flex justify-between pb-4">
          <span>
            {hasReachedLimit && (
              <p className="flex gap-2 text-muted-foreground items-center">
                <NormIcons.Info size={DEFAULT_ICON_SIZE} />
                You have reached the maximum of 5 free workspaces.{" "}
                <Link
                  to="/dash/$organizationSlug/pricing"
                  params={{ organizationSlug: organizationDto.slug }}
                >
                  <TextLinkSpan>Upgrade</TextLinkSpan>
                </Link>
                to create more workspaces.
              </p>
            )}
          </span>
          <Button
            disabled={hasReachedLimit}
            size="sm"
            onClick={() => {
              const id = generateWorkspaceName();
              createEmptyWorkspaceMutation.mutate({
                createWorkspaceDto: {
                  name: id,
                  slug: id,
                  access_level: "public_read",
                },
                onSuccess: () => {
                  toastSuccess("Workspace created");
                },
              });
            }}
          >
            <NormIcons.Add size={DEFAULT_ICON_SIZE} className="mr-2" />
            Create new
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {repositoryAdaptersQuery.data &&
            workspaceList.map((workspace) => (
              <WorkspaceCard
                key={workspace.id}
                workspace={workspace}
                organizationDto={organizationDto}
                isConnectedToVersionControl={repositoryAdaptersQuery.data.some(
                  (e) => e.workspace_id === workspace.id
                )}
              />
            ))}
        </div>
      </div>
    </div>
  );
}
